/**
 * 어시스트 드로어 크기 훅 — 저장·리사이즈 구독(D2 결정 4·5).
 *
 * 접기 훅(`useAssistCollapse`)과 **공유하지 않는다**: 두 축은 독립적으로
 * 바뀌고, 한 키에 묶으면 파싱 실패 하나가 둘 다 날린다. 공유하는 것은
 * 브레이크포인트 상수 하나뿐이다(새 축을 만들지 않는다).
 *
 * 창 리사이즈는 **표시만 clamp**하고 저장값은 건드리지 않는다 — 덮어쓰면
 * 외부 모니터를 물렸다 뺐다 할 때 취향이 한 방향으로만 조용히 깎인다.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ASSIST_SIZE_KEY,
  clampSize,
  defaultSize,
  sizeBounds,
  type AssistSize,
  type SizeBounds,
} from './assist-size'
import { ASSIST_OVERLAY_MAX_WIDTH } from './use-assist-collapse'

function viewport(): { vw: number; vh: number } {
  if (typeof window === 'undefined') return { vw: 1024, vh: 768 }
  return { vw: window.innerWidth, vh: window.innerHeight }
}

function isNarrow(vw: number): boolean {
  return vw < ASSIST_OVERLAY_MAX_WIDTH
}

function readStored(): unknown {
  // `localStorage` 접근 자체가 getter 로 던질 수 있다 — typeof 검사까지
  // try 안에 둔다(use-split-view.ts 와 같은 규율).
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem(ASSIST_SIZE_KEY)
  } catch {
    return null
  }
}

export function useAssistSize(): {
  /** 항상 clamp 된 값(렌더용) */
  size: AssistSize
  /** 너비 축을 그릴지 */
  narrow: boolean
  bounds: SizeBounds
  /** 드래그 중 — 저장하지 않는다 */
  preview: (next: Partial<AssistSize>) => void
  /** 확정(pointerup·키보드) — clamp 후 localStorage 기록 */
  commit: (next: Partial<AssistSize>) => void
  /** 기본값 복귀 */
  reset: () => void
} {
  const [vp, setVp] = useState(viewport)
  const narrow = isNarrow(vp.vw)
  const bounds = sizeBounds(vp.vw, vp.vh, narrow)
  /** 저장값(또는 세션 내 확정값) — 리사이즈가 이 값을 바꾸지 않는다. */
  const [stored, setStored] = useState<AssistSize>(() => {
    const { vw, vh } = viewport()
    return clampSize(readStored(), vw, vh, isNarrow(vw))
  })
  /** 드래그 중 미저장 표시값. */
  const [draft, setDraft] = useState<AssistSize | null>(null)

  useEffect(() => {
    const onResize = (): void => {
      setVp(viewport())
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const base = draft ?? stored
  const size = clampSize(base, vp.vw, vp.vh, narrow)

  const preview = useCallback((next: Partial<AssistSize>) => {
    setDraft((prev) => {
      const from = prev ?? stored
      return { w: next.w ?? from.w, h: next.h ?? from.h }
    })
  }, [stored])

  const commit = useCallback((next: Partial<AssistSize>) => {
    const { vw, vh } = viewport()
    setStored((prev) => {
      const from = draft ?? prev
      const merged = { w: next.w ?? from.w, h: next.h ?? from.h }
      const clamped = clampSize(merged, vw, vh, isNarrow(vw))
      try {
        localStorage.setItem(ASSIST_SIZE_KEY, JSON.stringify(clamped))
      } catch {
        // 저장 실패는 무시한다 — 이번 세션만 유지된다.
      }
      return clamped
    })
    setDraft(null)
  }, [draft])

  const reset = useCallback(() => {
    const { vw, vh } = viewport()
    const next = defaultSize(vw, vh, isNarrow(vw))
    setDraft(null)
    setStored(next)
    try {
      localStorage.setItem(ASSIST_SIZE_KEY, JSON.stringify(next))
    } catch {
      // 위와 같다.
    }
  }, [])

  return { size, narrow, bounds, preview, commit, reset }
}
